import { motion } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

const stats = [
  {
    value: '70',
    suffix: '%',
    label: 'of people research online before buying anything',
  },
  {
    value: '2.8',
    suffix: 'x',
    label: 'more leads with a real website vs social media alone',
  },
  {
    value: '60',
    suffix: '%',
    label: 'of your customers are on their phone right now',
  },
];

const AnimatedNumber = ({ value, suffix }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    const numericTarget = parseFloat(value);
    const duration = 1800;
    const steps = 50;
    const increment = numericTarget / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= numericTarget) {
        setCount(numericTarget);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {value.includes('.') ? count.toFixed(1) : Math.floor(count)}{suffix}
    </span>
  );
};

export const ProblemSection = () => {
  return (
    <section className="py-28 md:py-36 bg-[#0a0a0a] relative overflow-hidden" data-testid="problem-section">
      {/* Background */}
      <div className="absolute inset-0 cyber-grid opacity-[0.03]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Asymmetric two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {/* Left column - takes more space, offset */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="lg:col-span-7 lg:pr-12"
          >
            <motion.p 
              className="text-[#e62020] text-xs tracking-[0.2em] uppercase mb-5"
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              The problem
            </motion.p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase leading-[0.95] text-white"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              data-testid="problem-headline"
            >
              Most businesses
              <br />
              <span className="text-[#888888]">lose customers</span>
              <br />
              before they even
              <br />
              walk in the door.
            </h2>
            
            {/* Handwritten-style annotation */}
            <motion.p
              initial={{ opacity: 0, rotate: -2 }}
              whileInView={{ opacity: 1, rotate: -1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="text-[#e62020]/70 text-sm mt-6 ml-2 italic"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              ↑ and they don't even know it
            </motion.p>
          </motion.div>

          {/* Right column - stats, slight offset */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
            className="lg:col-span-5 lg:mt-16 space-y-10"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.12 }}
                className="relative pl-5"
                data-testid={`stat-${index}`}
              >
                {/* Varied border thickness */}
                <div 
                  className="absolute left-0 top-0 bottom-0 bg-[#e62020]"
                  style={{ width: index === 1 ? '3px' : '2px' }}
                />
                <span 
                  className="text-5xl sm:text-6xl font-bold text-[#e62020] block leading-none"
                  style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                >
                  <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                </span>
                <p className="text-[#888888] text-sm mt-2 leading-relaxed max-w-xs">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
